import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Chip,
  Stack,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  FitnessCenter as CoachingIcon,
  Home as HomeIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarTodayIcon,
  Group as GroupIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { coachingSessionAPI, amenityAPI } from '../../services/api';
import { CoachingSession, CoachingSessionBooking } from '../../types/coaching';
import { formatDate, formatTimeString } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import ReservationResultsDialog from './ReservationResultsDialog';

const ViewCoachingSessionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [sessionData, setSessionData] = useState<CoachingSession | null>(null);
  const [bookings, setBookings] = useState<CoachingSessionBooking[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingsLoading, setBookingsLoading] = useState<boolean>(false);
  const [missingVenues, setMissingVenues] = useState<string[]>([]);
  const missingBookingsControllerRef = useRef<AbortController | null>(null);
  const [rebooking, setRebooking] = useState<boolean>(false);
  const [reservationDialogOpen, setReservationDialogOpen] = useState<boolean>(false);
  const [reservationDialogData, setReservationDialogData] = useState<any>(null);

  // Abort any in-flight missing-bookings check on unmount
  useEffect(() => () => { missingBookingsControllerRef.current?.abort(); }, []);

  useEffect(() => {
    if (sessionId) {
      fetchSessionData();
    }
  }, [sessionId]);

  const fetchSessionData = async () => {
    if (!sessionId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await coachingSessionAPI.getCoachingSessionById(sessionId);
      setSessionData(response.data);
      
      // Check for missing amenity bookings if admin
      if ((user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'employee') && 
          response.data.venues && response.data.venues.length > 0) {
        checkMissingBookings(response.data);
      }
      
      // Also fetch bookings if user has appropriate permissions
      if (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach') {
        fetchBookings();
      }
    } catch (error: any) {
      console.error('Error fetching coaching session:', error);
      setError(error.response?.data?.message || 'Failed to fetch coaching session details');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    if (!sessionId) return;

    setBookingsLoading(true);
    try {
      const response = await coachingSessionAPI.getCoachingSessionBookings(sessionId);
      setBookings(response.data || []);
    } catch (error: any) {
      console.error('Error fetching bookings:', error);
      // Don't show booking errors as main error - they might not have permission
    } finally {
      setBookingsLoading(false);
    }
  };

  const checkMissingBookings = async (session: CoachingSession) => {
    missingBookingsControllerRef.current?.abort();
    const controller = new AbortController();
    missingBookingsControllerRef.current = controller;

    try {
      const instances = [{
        id: session.id,
        type: 'coachingSession' as const,
        venues: session.venues || [],
        sessionDate: session.sessionDate,
        startTime: session.startTime,
        endTime: session.endTime
      }];

      const response = await amenityAPI.checkMissingBookings(instances, controller.signal);
      const results = response.data.results || [];
      
      if (results.length > 0 && results[0].missingVenues) {
        setMissingVenues(results[0].missingVenues);
      } else {
        setMissingVenues([]);
      }
    } catch (error: any) {
      if (error?.code === 'ERR_CANCELED') return;
      console.error('Error checking for missing bookings:', error);
      setMissingVenues([]);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'SCHEDULED':
        return 'info';
      case 'ACTIVE':
        return 'success';
      case 'COMPLETED':
        return 'default';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getBookingStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'CONFIRMED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const canEditSession = () => {
    return user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach';
  };

  const handleEditSession = () => {
    // Navigate to dedicated edit page
    navigate(`/coaching/sessions/edit/${sessionId}`);
  };

  const handleRebookVenues = async () => {
    if (!sessionData || !sessionId) return;

    if (!window.confirm('This will rebook all venues for this session. Any existing venue bookings will be recreated. Continue?')) {
      return;
    }

    try {
      setRebooking(true);
      setError(null);

      // Call update with the current data to trigger venue rebooking
      const updateData = {
        name: sessionData.name,
        description: sessionData.description,
        sessionDate: sessionData.sessionDate,
        startTime: sessionData.startTime,
        endTime: sessionData.endTime,
        venues: sessionData.venues,
        capacity: sessionData.capacity,
        level: sessionData.level,
        coachId: sessionData.coachId,
        notes: sessionData.notes,
        minBookingDurationMinutes: sessionData.minBookingDurationMinutes,
        memberRateCost: sessionData.memberRateCost,
        memberRateDuration: sessionData.memberRateDuration,
        guestRateCost: sessionData.guestRateCost,
        guestRateDuration: sessionData.guestRateDuration,
        allowGuestBookings: sessionData.allowGuestBookings
      };

      const response = await coachingSessionAPI.updateCoachingSession(sessionId, updateData);
      
      // Check if reservation result is available
      if (response.data.reservationResult) {
        setReservationDialogData({
          entityName: sessionData.name,
          operationType: 'rebook',
          reservationResult: response.data.reservationResult
        });
        setReservationDialogOpen(true);
      }
      
      // Refresh data to check if bookings were created
      await fetchSessionData();
      
    } catch (error: any) {
      console.error('Error rebooking venues:', error);
      setError(error.response?.data?.message || 'Failed to rebook venues');
    } finally {
      setRebooking(false);
    }
  };

  const calculateSessionDuration = (startTime: string, endTime: string) => {
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diffMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (!sessionData) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="warning">Coaching session not found</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            startIcon={<HomeIcon />}
            sx={{ textTransform: 'none' }}
          >
            Dashboard
          </Button>
          <Button
            component={RouterLink}
            to="/calendar"
            color="inherit"
            startIcon={<CalendarTodayIcon />}
            sx={{ textTransform: 'none' }}
          >
            Calendar
          </Button>
          <Typography color="text.primary">Coaching Session Details</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              <CoachingIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              {sessionData.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {sessionData.description}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              variant="outlined"
            >
              Back
            </Button>
            {canEditSession() && (
              <Button
                startIcon={<EditIcon />}
                onClick={handleEditSession}
                variant="contained"
              >
                Edit Session
              </Button>
            )}
          </Stack>
        </Box>

        <Grid container spacing={3}>
          {/* Main Session Information */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Session Information
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CalendarTodayIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Date"
                        secondary={formatDate(sessionData.sessionDate)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <AccessTimeIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Time"
                        secondary={`${formatTimeString(sessionData.startTime)} - ${formatTimeString(sessionData.endTime)} (${calculateSessionDuration(sessionData.startTime, sessionData.endTime)})`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <LocationOnIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Venue"
                        secondary={
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {sessionData.venues && sessionData.venues.length > 0 ? `${sessionData.venues.length} venue(s)` : 'Not specified'}
                              </Typography>
                              {canEditSession() && sessionData.venues && sessionData.venues.length > 0 && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={handleRebookVenues}
                                  sx={{ textTransform: 'none', py: 0, px: 1, fontSize: '0.75rem' }}
                                >
                                  Rebook
                                </Button>
                              )}
                            </Box>
                            {canEditSession() && missingVenues.length > 0 && (
                              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <WarningIcon sx={{ fontSize: '0.9rem', color: '#ff9800' }} />
                                <Typography variant="caption" sx={{ color: '#ff9800', fontWeight: 'medium' }}>
                                  {missingVenues.length} court{missingVenues.length > 1 ? 's' : ''} missing booking{missingVenues.length > 1 ? 's' : ''}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Coach"
                        secondary={sessionData.coachName || 'Not assigned'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CoachingIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Level"
                        secondary={sessionData.level}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <GroupIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Capacity"
                        secondary={`${bookings.filter(b => b.status === 'CONFIRMED').length} / ${sessionData.capacity} bookings`}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
              
              {/* Booking Information */}
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Booking Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Minimum Booking Duration:</strong> {sessionData.minBookingDurationMinutes} minutes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Guest Bookings:</strong> {sessionData.allowGuestBookings ? 'Allowed' : 'Not allowed'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    {(sessionData.memberRateCost || sessionData.guestRateCost) && (
                      <Box>
                        {sessionData.memberRateCost && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Member Rate:</strong> ${sessionData.memberRateCost} per {sessionData.memberRateDuration} minutes
                          </Typography>
                        )}
                        {sessionData.guestRateCost && sessionData.allowGuestBookings && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Guest Rate:</strong> ${sessionData.guestRateCost} per {sessionData.guestRateDuration} minutes
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>

              {sessionData.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Notes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {sessionData.notes}
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>

            {/* Bookings Section (for admin/coach users) */}
            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach') && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Session Bookings ({bookings.length})
                </Typography>
                {bookingsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : bookings.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Client</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Time Slot</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Cost</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Booked At</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bookings.map((booking) => (
                          <TableRow key={booking.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                                  {booking.isGuestBooking 
                                    ? (booking.guestName?.charAt(0) || 'G')
                                    : (booking.memberName?.charAt(0) || booking.memberEmail?.charAt(0))
                                  }
                                </Avatar>
                                {booking.isGuestBooking 
                                  ? (booking.guestName || 'Guest')
                                  : (booking.memberName || 'Unknown')
                                }
                                {booking.isGuestBooking && (
                                  <Chip label="Guest" size="small" sx={{ ml: 1 }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {booking.isGuestBooking 
                                ? booking.guestEmail 
                                : booking.memberEmail
                              }
                            </TableCell>
                            <TableCell>
                              {formatTimeString(booking.startTime)} - {formatTimeString(booking.endTime)}
                            </TableCell>
                            <TableCell>{booking.durationMinutes} minutes</TableCell>
                            <TableCell>
                              {booking.totalCost ? `$${booking.totalCost}` : 'N/A'}
                              {booking.paid && (
                                <Chip label="Paid" color="success" size="small" sx={{ ml: 1 }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={booking.status}
                                color={getBookingStatusColor(booking.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(booking.createdAt).toLocaleDateString()} {new Date(booking.createdAt).toLocaleTimeString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No bookings for this session yet.
                  </Typography>
                )}
              </Paper>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={3}>
              {/* Status Card */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    label={sessionData.status}
                    color={getStatusColor(sessionData.status)}
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Active: {sessionData.active ? 'Yes' : 'No'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Created: {new Date(sessionData.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Updated: {new Date(sessionData.updatedAt).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>

              {/* Booking Status Card */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Booking Status
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h3" color="primary">
                      {bookings.filter(b => b.status === 'CONFIRMED').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      of {sessionData.capacity} spots booked
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      Available: {sessionData.capacity - bookings.filter(b => b.status === 'CONFIRMED').length}
                    </Typography>
                    <Typography variant="body2">
                      {Math.round((bookings.filter(b => b.status === 'CONFIRMED').length / sessionData.capacity) * 100)}% booked
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 8,
                      backgroundColor: 'grey.300',
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.min((bookings.filter(b => b.status === 'CONFIRMED').length / sessionData.capacity) * 100, 100)}%`,
                        height: '100%',
                        backgroundColor: (bookings.filter(b => b.status === 'CONFIRMED').length / sessionData.capacity) >= 0.8 ? 'error.main' : 'primary.main',
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </Box>
                  {bookings.filter(b => b.status === 'CANCELLED').length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Cancelled: {bookings.filter(b => b.status === 'CANCELLED').length} bookings
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              {canEditSession() && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Quick Actions
                    </Typography>
                    <Stack spacing={1}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={handleEditSession}
                      >
                        Edit Session
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<PeopleIcon />}
                        onClick={() => navigate('/coaching/sessions')}
                      >
                        Manage Bookings
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Reservation Results Dialog */}
      {reservationDialogData && (
        <ReservationResultsDialog
          open={reservationDialogOpen}
          onClose={() => {
            setReservationDialogOpen(false);
            setReservationDialogData(null);
          }}
          entityType="session"
          entityName={reservationDialogData.entityName}
          operationType={reservationDialogData.operationType}
          reservationResult={reservationDialogData.reservationResult}
        />
      )}
    </Container>
  );
};

export default ViewCoachingSessionPage;